/**
 * Removes the trailing slashes from an internal path and keeps the query and the hash.
 * The function returns each string that does not start with `/` without changes.
 *
 * The function changes only slash characters. `new URL()` encodes the path again and resolves dot segments.
 * The route middleware compares the result with `to.path`, so a different encoding causes a redirect loop.
 *
 * The function changes leading slashes to one slash. A path that starts with `//` is a protocol-relative URL
 * to a different host (open redirect), and `navigateTo` throws an error for it.
 */
export function normalizeInternalPath(path: string): string {
    if (!path.startsWith('/')) {
        return path;
    }

    const suffixStartIndex = path.search(/[?#]/);
    const pathPart = suffixStartIndex === -1 ? path : path.slice(0, suffixStartIndex);
    const suffix = suffixStartIndex === -1 ? '' : path.slice(suffixStartIndex);

    const collapsedPathPart = pathPart.replace(/^\/+/, '/');

    // A loop instead of a regular expression, because `/\/+$/` is slow for a long path with many slashes.
    let endIndex = collapsedPathPart.length;
    while (endIndex > 1 && collapsedPathPart[endIndex - 1] === '/') {
        endIndex--;
    }

    return `${collapsedPathPart.slice(0, endIndex)}${suffix}`;
}
